import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import MaterialTooltip from '@material-ui/core/Tooltip'
import { getAppTheme } from 'utilities/utils'

const ChildContainer = React.forwardRef((props, ref) => (
  <span {...props} ref={ref} style={{ display: 'inline-block' }} />
))

const { theme } = getAppTheme()

// Using withStyles instead of styled-components here since there
// does not seem to be a way to style the tooltip itself (instead
// of the wrapper component) without withStyles.
const PlainTooltip = withStyles(() => ({
  tooltip: {
    fontFamily: theme.font,
    fontSize: 12,
    color: theme.colors.textOnDarkBackground,
    opacity: 1,
    fontWeight: 400,
    maxWidth: (props) => props.width || '50em',
    ...(theme.colors.tooltipBackground && {
      backgroundColor: theme.colors.tooltipBackground,
    }),
  },
  arrow: {
    ...(theme.colors.tooltipBackground && {
      color: theme.colors.tooltipBackground,
    }),
  },
}))(MaterialTooltip)

const RichTooltip = withStyles(() => ({
  tooltip: {
    fontFamily: theme.font,
    fontSize: 12,
    color: theme.colors.text,
    backgroundColor: theme.colors.sectionBackground,
    opacity: 1,
    fontWeight: 400,
    maxWidth: (props) => props.width || '50em', // no wider than about 60 characters per line, which is still fairly wide.
    boxShadow: theme.shadows.bottomRight,
    padding: `${theme.sizings.lvl2} ${theme.sizings.lvl3}`,
  },
  arrow: {
    color: theme.colors.sectionBackground,
  },
}))(MaterialTooltip)

/**
 * General tooltip. Replaces the old Tooltip component.
 * variant="plain" (default): solid dark background, white text.
 * variant="rich": white card-like background, dark text, for structured/JSX content.
 */
function NewTooltip({ children, variant, ...props }) {
  const TooltipComponent = variant === 'rich' ? RichTooltip : PlainTooltip
  return (
    <TooltipComponent {...props}>
      <ChildContainer>{children}</ChildContainer>
    </TooltipComponent>
  )
}

NewTooltip.propTypes = {
  children: PropTypes.element.isRequired,
  /** The text to be shown in the tooltip */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  /** plain: solid dark bg + white text. rich: white card-like bg + dark text. */
  variant: PropTypes.oneOf(['plain', 'rich']),
  placement: PropTypes.oneOf([
    'top-start',
    'top',
    'top-end',
    'left-start',
    'left',
    'left-end',
    'right-start',
    'right',
    'right-end',
    'bottom-start',
    'bottom',
    'bottom-end',
  ]),
  arrow: PropTypes.bool,
  width: PropTypes.number,
}

NewTooltip.defaultProps = {
  variant: 'plain',
  placement: 'bottom',
  arrow: false,
  width: undefined,
}

export default NewTooltip
